import React from 'react';
import useProfileConnectedHealthSystems from '../../../../../hooks/api/useProfileConnectedHealthSystems';
import Alert from '../../../../v2BaseComponents/Alert';
import { getIntl } from '../../../../../utils/intl';
import { usePromptButton } from '../../../../shared/PromptButton';
import DeleteConfirmationPromptContents from './DeleteConfirmationPromptContents';
import getName from '../../../../../utils/getName';
import getDateString from '../../../../../utils/getDateString';
import { useHasPermission } from '../../../../v2BaseComponents/Permissions/hooks';
import { useGoBack } from '../../../../../utils/appNavigationNames';
import useOneUpHealthRefreshProgress from '../../../../../hooks/api/useMedicalProfileRefreshProgress';
import Stack, { ContentStack } from '../../../../v2BaseComponents/Stack';
import ListItem from '../../../../v2BaseComponents/ListItem';
import Text from '../../../../v2BaseComponents/Text';

const intl = getIntl('patientMismatchAlert', {
    patientMismatchAlertTitle: 'Patient Mismatch',
    patientMismatchAlertMessage:
        'This profile does not completely match the patient associated with this portal.  The following attributes do not match:',
    patientName: 'Patient Name - {name}',
    patientDateOfBirth: 'Patient Date of Birth - {dateOfBirth}',
    subText: 'You can either confirm this mismatch or delete this connection.',
    refreshInProgressSubtext:
        'You can either confirm this mismatch or delete this connection once the refresh is complete.',
    patientMismatchAlertConfirm: 'Confirm',
    patientMismatchAlertDelete: 'Delete',
    disconnectFromPortal: 'Disconnect from Portal?',
});

export type PatientMismatchAlertOwnProps = {
    medicalProfileId: string;
    healthSystemId: string | number;
};

export type PatientMismatchAlertProps = PatientMismatchAlertOwnProps;

const PatientMismatchAlert: React.FC<PatientMismatchAlertProps> = ({ medicalProfileId, healthSystemId }) => {
    const goBack = useGoBack();
    const [hasConfirmed, setHasConfirmed] = React.useState(false);
    const {
        healthSystemConnectionDetails,
        disconnectHealthSystem,
        isConfirmingPatientMismatch,
        isLoadingHealthSystemConnectionDetails,
        confirmPatientMismatch,
    } = useProfileConnectedHealthSystems({
        medicalProfileId,
        healthSystemId: String(healthSystemId),
    });
    const { isRefreshing } = useOneUpHealthRefreshProgress({
        medicalProfileId,
    });
    const hasPortalEditPermission = useHasPermission(medicalProfileId, 'patientPortals', 'edit');
    const deletePromptButton = usePromptButton({
        renderPromptContents: (onClose) => (
            <DeleteConfirmationPromptContents
                medicalProfileId={medicalProfileId}
                healthSystemId={healthSystemId}
                onClose={onClose}
                onDisconnect={() => {
                    disconnectHealthSystem(String(healthSystemId));
                    goBack();
                }}
            />
        ),
        promptTitle: intl('disconnectFromPortal'),
    });

    if (
        isLoadingHealthSystemConnectionDetails ||
        isConfirmingPatientMismatch ||
        !healthSystemConnectionDetails?.patientMismatchProperties?.length ||
        healthSystemConnectionDetails?.hasMisMatchBeenConfirmed ||
        !hasPortalEditPermission ||
        hasConfirmed
    )
        return null;

    return (
        <>
            <Alert
                justifyActionItems="space-around"
                type="warning"
                title={intl('patientMismatchAlertTitle')}
                actionItems={[
                    {
                        title: intl('patientMismatchAlertDelete'),
                        onPress: deletePromptButton.onPress,
                        isDisabled: isRefreshing,
                    },
                    {
                        title: intl('patientMismatchAlertConfirm'),
                        onPress: () => {
                            setHasConfirmed(true);
                            confirmPatientMismatch(String(healthSystemId));
                        },
                    },
                ]}
                content={
                    <ContentStack>
                        <Text variant="body-1">{intl('patientMismatchAlertMessage')}</Text>
                        <Stack vertical fullWidth>
                            {healthSystemConnectionDetails?.patientMismatchProperties?.includes('name') ? (
                                <ListItem unordered variant="body-1">
                                    {intl('patientName', {
                                        name: getName({
                                            firstName: healthSystemConnectionDetails?.name?.first,
                                            lastName: healthSystemConnectionDetails?.name?.last,
                                        }),
                                    })}
                                </ListItem>
                            ) : null}
                            {healthSystemConnectionDetails?.patientMismatchProperties?.includes('dob') &&
                            healthSystemConnectionDetails?.dob ? (
                                <ListItem unordered variant="body-1">
                                    {intl('patientDateOfBirth', {
                                        dateOfBirth: getDateString(healthSystemConnectionDetails.dob, 'date'),
                                    })}
                                </ListItem>
                            ) : null}
                        </Stack>
                        <Text variant="body-1">{intl(!isRefreshing ? 'subText' : 'refreshInProgressSubtext')}</Text>
                    </ContentStack>
                }
            />
            {deletePromptButton.Prompt}
        </>
    );
};

export default React.memo(PatientMismatchAlert);
